/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { PREDICTION_MIGRATOR_PROGRAM_ADDRESS } from '../programs';

export const PREVIEW_PAYOUT_IF_WINNER_DISCRIMINATOR = new Uint8Array([
  228, 144, 72, 185, 234, 186, 183, 143,
]);

export function getPreviewPayoutIfWinnerDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    PREVIEW_PAYOUT_IF_WINNER_DISCRIMINATOR,
  );
}

export type PreviewPayoutIfWinnerInstruction<
  TProgram extends string = typeof PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
  TAccountMarket extends string | AccountMeta<string> = string,
  TAccountCandidateMint extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountMarket extends string
        ? ReadonlyAccount<TAccountMarket>
        : TAccountMarket,
      TAccountCandidateMint extends string
        ? ReadonlyAccount<TAccountCandidateMint>
        : TAccountCandidateMint,
      ...TRemainingAccounts,
    ]
  >;

export type PreviewPayoutIfWinnerInstructionData = {
  discriminator: ReadonlyUint8Array;
  candidateWinnerMint: Address;
  tokenAmount: bigint;
};

export type PreviewPayoutIfWinnerInstructionDataArgs = {
  candidateWinnerMint: Address;
  tokenAmount: number | bigint;
};

export function getPreviewPayoutIfWinnerInstructionDataEncoder(): FixedSizeEncoder<PreviewPayoutIfWinnerInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['candidateWinnerMint', getAddressEncoder()],
      ['tokenAmount', getU64Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: PREVIEW_PAYOUT_IF_WINNER_DISCRIMINATOR,
    }),
  );
}

export function getPreviewPayoutIfWinnerInstructionDataDecoder(): FixedSizeDecoder<PreviewPayoutIfWinnerInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['candidateWinnerMint', getAddressDecoder()],
    ['tokenAmount', getU64Decoder()],
  ]);
}

export function getPreviewPayoutIfWinnerInstructionDataCodec(): FixedSizeCodec<
  PreviewPayoutIfWinnerInstructionDataArgs,
  PreviewPayoutIfWinnerInstructionData
> {
  return combineCodec(
    getPreviewPayoutIfWinnerInstructionDataEncoder(),
    getPreviewPayoutIfWinnerInstructionDataDecoder(),
  );
}

export type PreviewPayoutIfWinnerInput<
  TAccountMarket extends string = string,
  TAccountCandidateMint extends string = string,
> = {
  /** Market PDA */
  market: Address<TAccountMarket>;
  /** Candidate winner mint (may or may not match oracle winner) */
  candidateMint: Address<TAccountCandidateMint>;
  candidateWinnerMint: PreviewPayoutIfWinnerInstructionDataArgs['candidateWinnerMint'];
  tokenAmount: PreviewPayoutIfWinnerInstructionDataArgs['tokenAmount'];
};

export function getPreviewPayoutIfWinnerInstruction<
  TAccountMarket extends string,
  TAccountCandidateMint extends string,
  TProgramAddress extends Address = typeof PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
>(
  input: PreviewPayoutIfWinnerInput<TAccountMarket, TAccountCandidateMint>,
  config?: { programAddress?: TProgramAddress },
): PreviewPayoutIfWinnerInstruction<
  TProgramAddress,
  TAccountMarket,
  TAccountCandidateMint
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? PREDICTION_MIGRATOR_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    market: { value: input.market ?? null, isWritable: false },
    candidateMint: { value: input.candidateMint ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('market', accounts.market),
      getAccountMeta('candidateMint', accounts.candidateMint),
    ],
    data: getPreviewPayoutIfWinnerInstructionDataEncoder().encode(
      args as PreviewPayoutIfWinnerInstructionDataArgs,
    ),
    programAddress,
  } as PreviewPayoutIfWinnerInstruction<
    TProgramAddress,
    TAccountMarket,
    TAccountCandidateMint
  >);
}

export type ParsedPreviewPayoutIfWinnerInstruction<
  TProgram extends string = typeof PREDICTION_MIGRATOR_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Market PDA */
    market: TAccountMetas[0];
    /** Candidate winner mint (may or may not match oracle winner) */
    candidateMint: TAccountMetas[1];
  };
  data: PreviewPayoutIfWinnerInstructionData;
};

export function parsePreviewPayoutIfWinnerInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedPreviewPayoutIfWinnerInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 2,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { market: getNextAccount(), candidateMint: getNextAccount() },
    data: getPreviewPayoutIfWinnerInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
